import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API } from "../App";
import { Sidebar } from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { toast } from "sonner";
import { 
  Users,
  FileSpreadsheet,
  Download,
  Search,
  Calendar,
  CheckCircle2,
  Hourglass,
  XCircle,
  Filter,
  Upload,
  KeyRound,
  Copy,
  FileUp,
  Menu,
  BarChart3,
  TrendingUp
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function RHDashboard() {
  const [users, setUsers] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [invitationFilter, setInvitationFilter] = useState("all");
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [advancedStats, setAdvancedStats] = useState(null);
  const fileInputRef = useRef(null);
  
  const { getAuthHeader, token } = useAuth();

  const fetchData = async () => {
    try {
      const [usersRes, requestsRes, statsRes, invitationsRes, advRes] = await Promise.all([
        axios.get(`${API}/users`, getAuthHeader()),
        axios.get(`${API}/leave-requests/all`, getAuthHeader()),
        axios.get(`${API}/stats/summary`, getAuthHeader()),
        axios.get(`${API}/invitations`, getAuthHeader()),
        axios.get(`${API}/stats/advanced`, getAuthHeader())
      ]);
      setUsers(usersRes.data);
      setAllRequests(requestsRes.data);
      setStats(statsRes.data);
      setInvitations(invitationsRes.data);
      setAdvancedStats(advRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportLoading(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/invitations/import`, formData, {
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      setImportResult(response.data);
      toast.success(`Import réussi: ${response.data.codes_created} codes créés`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(
        `${API}/export/leaves?month=${exportMonth}&year=${exportYear}`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conges_${exportYear}_${String(exportMonth).padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export téléchargé avec succès!");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportInvitations = async () => {
    try {
      const response = await axios.get(`${API}/export/invitations`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `codes_invitation_${format(new Date(), 'yyyyMMdd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Codes exportés!");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Code copié!");
  };

  const filteredRequests = allRequests.filter(request => {
    const matchesSearch = request.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          request.user_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || request.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredInvitations = invitations.filter(inv => {
    const matchesSearch = inv.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          inv.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          inv.matricule.includes(searchTerm);
    const matchesFilter = invitationFilter === "all" || 
                          (invitationFilter === "used" && inv.used) ||
                          (invitationFilter === "unused" && !inv.used);
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: { class: "bg-yellow-50 text-yellow-700 border-yellow-200", icon: Hourglass, label: "En attente" },
      approved: { class: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2, label: "Approuvé" },
      rejected: { class: "bg-red-50 text-red-700 border-red-200", icon: XCircle, label: "Refusé" }
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge variant="outline" className={`${style.class} border`}>
        <style.icon className="h-3 w-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      collaborateur: "bg-blue-50 text-blue-700 border-blue-200",
      manager: "bg-purple-50 text-purple-700 border-purple-200",
      rh: "bg-[#00A3E0]/10 text-[#00A3E0] border-[#00A3E0]/20"
    };
    return (
      <Badge variant="outline" className={`${styles[role]} border capitalize`}>
        {role}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <div className="hidden lg:block"><Sidebar /></div>
        <main className="flex-1 bg-gray-50 p-4 lg:p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-24 lg:h-32 bg-gray-200 rounded-lg"></div>)}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block"><Sidebar /></div>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar />
        </SheetContent>
      </Sheet>

      <main className="flex-1 bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-4 lg:px-8 py-4 lg:py-6 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileMenuOpen(true)}>
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg lg:text-2xl font-bold text-[#1a2634]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Administration RH
                </h1>
                <p className="text-gray-500 text-sm hidden sm:block">Vue globale et gestion des congés</p>
              </div>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">
          {/* Stats cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-6 lg:mb-8">
            <Card className="stat-card border-0 shadow-sm" data-testid="stat-total-users">
              <CardContent className="p-3 lg:pt-6 lg:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="text-center lg:text-left">
                    <p className="text-[10px] lg:text-sm font-medium text-gray-500 uppercase tracking-wide">Inscrits</p>
                    <p className="text-2xl lg:text-4xl font-black text-[#1a2634] mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {stats?.total_users || 0}
                    </p>
                  </div>
                  <div className="hidden lg:block p-3 bg-blue-50 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card border-0 shadow-sm" data-testid="stat-pending-requests">
              <CardContent className="p-3 lg:pt-6 lg:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="text-center lg:text-left">
                    <p className="text-[10px] lg:text-sm font-medium text-gray-500 uppercase tracking-wide">En attente</p>
                    <p className="text-2xl lg:text-4xl font-black text-amber-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {stats?.pending_requests || 0}
                    </p>
                  </div>
                  <div className="hidden lg:block p-3 bg-amber-50 rounded-lg">
                    <Hourglass className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card border-0 shadow-sm" data-testid="stat-invitations">
              <CardContent className="p-3 lg:pt-6 lg:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="text-center lg:text-left">
                    <p className="text-[10px] lg:text-sm font-medium text-gray-500 uppercase tracking-wide">Codes libres</p>
                    <p className="text-2xl lg:text-4xl font-black text-purple-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {stats?.unused_invitations || 0}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5 hidden sm:block">sur {stats?.total_invitations || 0} générés</p>
                  </div>
                  <div className="hidden lg:block p-3 bg-purple-50 rounded-lg">
                    <KeyRound className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card border-0 shadow-sm" data-testid="stat-approved-month">
              <CardContent className="p-3 lg:pt-6 lg:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="text-center lg:text-left">
                    <p className="text-[10px] lg:text-sm font-medium text-gray-500 uppercase tracking-wide">Ce mois</p>
                    <p className="text-2xl lg:text-4xl font-black text-emerald-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {stats?.approved_this_month || 0}
                    </p>
                  </div>
                  <div className="hidden lg:block p-3 bg-emerald-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="invitations" className="space-y-4 lg:space-y-6">
            <TabsList className="bg-white border shadow-sm w-full lg:w-auto overflow-x-auto">
              <TabsTrigger value="invitations" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm" data-testid="tab-invitations">
                <KeyRound className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Codes</span>
                <span className="sm:hidden">Codes</span>
              </TabsTrigger>
              <TabsTrigger value="requests" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm" data-testid="tab-all-requests">
                <Calendar className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Demandes</span>
                <span className="sm:hidden">Congés</span>
              </TabsTrigger>
              <TabsTrigger value="employees" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm" data-testid="tab-employees">
                <Users className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Collaborateurs</span>
                <span className="sm:hidden">Equipe</span>
              </TabsTrigger>
              <TabsTrigger value="export" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm" data-testid="tab-export">
                <FileSpreadsheet className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                Export
              </TabsTrigger>
              <TabsTrigger value="stats" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm" data-testid="tab-stats">
                <BarChart3 className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Statistiques</span>
                <span className="sm:hidden">Stats</span>
              </TabsTrigger>
            </TabsList>

            {/* Invitations management */}
            <TabsContent value="invitations">
              <div className="space-y-6">
                {/* Import section */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="border-b border-gray-100">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Upload className="h-5 w-5 text-[#00A3E0]" />
                      Import des collaborateurs
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex-1">
                        <p className="text-gray-600 mb-4">
                          Importez votre fichier Excel de soldes de congés pour générer automatiquement les codes d'invitation.
                        </p>
                        <div className="flex gap-3">
                          <input
                            type="file"
                            ref={fileInputRef}
                            accept=".xlsx,.xls"
                            onChange={handleImportExcel}
                            className="hidden"
                            id="excel-import"
                          />
                          <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={importLoading}
                            className="bg-[#00A3E0] hover:bg-[#0090c7] text-white"
                            data-testid="import-excel-btn"
                          >
                            {importLoading ? (
                              <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Import en cours...
                              </div>
                            ) : (
                              <>
                                <FileUp className="h-4 w-4 mr-2" />
                                Importer fichier Excel
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleExportInvitations}
                            data-testid="export-invitations-btn"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Exporter les codes
                          </Button>
                        </div>
                      </div>
                      
                      {importResult && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <h4 className="font-semibold text-green-800 mb-2">Résultat de l'import</h4>
                          <ul className="text-sm text-green-700 space-y-1">
                            <li>✓ {importResult.codes_created} codes créés</li>
                            <li>✓ {importResult.balances_updated} soldes mis à jour</li>
                            {importResult.errors?.length > 0 && (
                              <li className="text-orange-600">⚠ {importResult.errors.length} erreurs</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Invitations list */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="border-b border-gray-100">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <CardTitle className="text-lg font-semibold">Codes d'invitation ({filteredInvitations.length})</CardTitle>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="Rechercher par nom ou matricule..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 w-64"
                            data-testid="search-invitations"
                          />
                        </div>
                        <Select value={invitationFilter} onValueChange={setInvitationFilter}>
                          <SelectTrigger className="w-40">
                            <Filter className="h-4 w-4 mr-2" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tous</SelectItem>
                            <SelectItem value="unused">Non utilisés</SelectItem>
                            <SelectItem value="used">Utilisés</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead>Matricule</TableHead>
                            <TableHead>Collaborateur</TableHead>
                            <TableHead>Concession</TableHead>
                            <TableHead>Code d'invitation</TableHead>
                            <TableHead>Statut</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredInvitations.map((inv) => (
                            <TableRow key={inv.code} data-testid={`invitation-${inv.matricule}`}>
                              <TableCell className="font-mono text-sm">{inv.matricule}</TableCell>
                              <TableCell className="font-medium">
                                {inv.first_name} {inv.last_name}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">{inv.concession}</TableCell>
                              <TableCell>
                                <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                                  {inv.code}
                                </code>
                              </TableCell>
                              <TableCell>
                                {inv.used ? (
                                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Inscrit
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-200">
                                    En attente
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {!inv.used && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => copyToClipboard(inv.code)}
                                    data-testid={`copy-code-${inv.matricule}`}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* All requests */}
            <TabsContent value="requests">
              <Card className="border-0 shadow-sm">
                <CardHeader className="border-b border-gray-100">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <CardTitle className="text-lg font-semibold">Toutes les demandes de congés</CardTitle>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Rechercher..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 w-64"
                          data-testid="search-requests"
                        />
                      </div>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-40" data-testid="filter-status">
                          <Filter className="h-4 w-4 mr-2" />
                          <SelectValue placeholder="Statut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous</SelectItem>
                          <SelectItem value="pending">En attente</SelectItem>
                          <SelectItem value="approved">Approuvés</SelectItem>
                          <SelectItem value="rejected">Refusés</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Collaborateur</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Période</TableHead>
                          <TableHead>Durée</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Date demande</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRequests.map((request) => (
                          <TableRow key={request.id} data-testid={`rh-request-${request.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{request.user_name}</p>
                                <p className="text-xs text-gray-500">{request.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{request.leave_type_name}</TableCell>
                            <TableCell>
                              {format(new Date(request.start_date), "dd/MM/yyyy")} - {format(new Date(request.end_date), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                {request.days_count} j
                              </Badge>
                            </TableCell>
                            <TableCell>{getStatusBadge(request.status)}</TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {format(new Date(request.created_at), "dd/MM/yyyy HH:mm")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Employees */}
            <TabsContent value="employees">
              <Card className="border-0 shadow-sm">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="text-lg font-semibold">Collaborateurs inscrits ({users.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Matricule</TableHead>
                          <TableHead>Nom</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Concession</TableHead>
                          <TableHead>Rôle</TableHead>
                          <TableHead>Date inscription</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.id} data-testid={`employee-${user.id}`}>
                            <TableCell className="font-mono text-sm">{user.matricule || "-"}</TableCell>
                            <TableCell className="font-medium">
                              {user.first_name} {user.last_name}
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.concession || "-"}</TableCell>
                            <TableCell>{getRoleBadge(user.role)}</TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {format(new Date(user.created_at), "dd/MM/yyyy")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Export */}
            <TabsContent value="export">
              <Card className="border-0 shadow-sm">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-[#00A3E0]" />
                    Export des congés pour la paie
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="max-w-md space-y-6">
                    <p className="text-gray-600">
                      Exportez les congés approuvés au format Excel pour transmission à votre service de paie.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Mois</label>
                        <Select value={String(exportMonth)} onValueChange={(v) => setExportMonth(parseInt(v))}>
                          <SelectTrigger data-testid="export-month">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 12 }, (_, i) => (
                              <SelectItem key={i + 1} value={String(i + 1)}>
                                {format(new Date(2024, i, 1), "MMMM", { locale: fr })}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Année</label>
                        <Select value={String(exportYear)} onValueChange={(v) => setExportYear(parseInt(v))}>
                          <SelectTrigger data-testid="export-year">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026].map((year) => (
                              <SelectItem key={year} value={String(year)}>
                                {year}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button
                      onClick={handleExport}
                      disabled={exportLoading}
                      className="w-full bg-[#00A3E0] hover:bg-[#0090c7] text-white shadow-lg shadow-cyan-500/20"
                      data-testid="export-button"
                    >
                      {exportLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Génération...
                        </div>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Télécharger l'export Excel
                        </>
                      )}
                    </Button>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Format de l'export</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• <strong>Onglet Congés</strong> : Matricule, Nom, Email, Concession, Type, Dates, Jours, Demi-journées</li>
                        <li>• <strong>Onglet Soldes CP</strong> : Matricule, Nom, CP N-1/N-2/Ancienneté (acquis, pris, restant)</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Stats */}
            <TabsContent value="stats">
              {advancedStats ? (
                <div className="space-y-6">
                  {/* Monthly bar chart */}
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="border-b border-gray-100">
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-[#00A3E0]" />
                        Jours d'absence par mois — {advancedStats.year}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 lg:p-6">
                      <div className="flex items-end gap-1.5 lg:gap-3 h-48 lg:h-64" data-testid="monthly-chart">
                        {advancedStats.monthly_absences.map((m) => {
                          const maxDays = Math.max(...advancedStats.monthly_absences.map(x => x.total_days), 1);
                          const pct = (m.total_days / maxDays) * 100;
                          const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
                          const isCurrentMonth = m.month === new Date().getMonth() + 1;
                          return (
                            <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-[10px] lg:text-xs font-semibold text-gray-700">{m.total_days > 0 ? m.total_days : ''}</span>
                              <div className="w-full flex items-end" style={{ height: '100%' }}>
                                <div
                                  className={`w-full rounded-t transition-all ${isCurrentMonth ? 'bg-[#00A3E0]' : m.total_days > 0 ? 'bg-[#00A3E0]/60' : 'bg-gray-100'}`}
                                  style={{ height: `${Math.max(pct, 2)}%` }}
                                />
                              </div>
                              <span className={`text-[9px] lg:text-xs ${isCurrentMonth ? 'font-bold text-[#00A3E0]' : 'text-gray-500'}`}>
                                {monthNames[m.month - 1]}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* By leave type */}
                    <Card className="border-0 shadow-sm">
                      <CardHeader className="border-b border-gray-100">
                        <CardTitle className="text-base font-semibold">Répartition par type de congé</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 lg:p-6">
                        {advancedStats.by_type.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-8">Aucune donnée</p>
                        ) : (
                          <div className="space-y-3" data-testid="type-distribution">
                            {advancedStats.by_type.map((t, i) => {
                              const maxDays = Math.max(...advancedStats.by_type.map(x => x.total_days), 1);
                              const colors = ['bg-[#00A3E0]', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500', 'bg-red-400', 'bg-indigo-500'];
                              return (
                                <div key={i}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-700">{t.type}</span>
                                    <span className="text-sm text-gray-500">{t.total_days}j ({t.count} demande{t.count > 1 ? 's' : ''})</span>
                                  </div>
                                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${colors[i % colors.length]}`} style={{ width: `${(t.total_days / maxDays) * 100}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Top absents */}
                    <Card className="border-0 shadow-sm">
                      <CardHeader className="border-b border-gray-100">
                        <CardTitle className="text-base font-semibold">Collaborateurs les plus absents</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 lg:p-6">
                        {advancedStats.top_absents.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-8">Aucune donnée</p>
                        ) : (
                          <div className="space-y-2.5" data-testid="top-absents">
                            {advancedStats.top_absents.map((u, i) => (
                              <div key={i} className="flex items-center gap-3">
                                <div className="w-7 h-7 rounded-full bg-[#1a2634] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                  {u.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                                </div>
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 flex-shrink-0">
                                  {u.total_days}j
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ) : (
                <div className="text-center py-16 text-gray-400">
                  <BarChart3 className="h-10 w-10 mx-auto mb-3" />
                  <p className="text-sm">Chargement des statistiques...</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
